// Need to use the React-specific entry point to import createApi
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

// Define a service using a base URL and expected endpoints
export const coursesData = createApi({
  reducerPath: "coursesApi",
  baseQuery: fetchBaseQuery({ baseUrl: process.env.REACT_APP_API_URL }),
  endpoints: (builder) => ({
    getFreeResources: builder.query({
      query: ({ class_id, subject_id }) => ({
        url: `free_resource`,
        params: { class_id, subject_id },
      }),
      transformResponse: (response, meta, arg) => response.data,
    }),
    getCourseDetail: builder.query({
      query: (product_id) => ({
        url: `course_details`,
        params: { product_id },
      }),
      transformResponse: (response, meta, arg) => response.data,
    }),
    getCoursePlans: builder.query({
      query: (product_id) => ({
        url: `course_plan`,
        params: { product_id },
      }),
      transformResponse: (response, meta, arg) => response.data,
    }),
    getStudentFeedback: builder.query({
      query: () => `student_feedback`,
      transformResponse: (response, meta, arg) => response.data,
    }),
    getUltimateLearning: builder.query({
      query: () => `ultimate`,
      transformResponse: (response, meta, arg) => response.data,
    }),
    getCoursesBySubject: builder.query({
      query: ({ batchID, learningMode, classID, search, limit, offset }) => ({
        url: `get_course_subject`,
        params: {
          batch_id: batchID,
          learning_mode: learningMode,
          class_id: classID,
          search,
          limit,
          offset,
        },
      }),
      transformResponse: (response, meta, arg) => response.data,
      serializeQueryArgs: ({ queryArgs }) => {
        const newQueryArgs = { ...queryArgs };

        if (newQueryArgs.limit) {
          delete newQueryArgs.limit;
          delete newQueryArgs.offset;
        }

        return newQueryArgs;
      },
      merge: (currentCache, newItems, other) => {
        if (!other.arg.offset) {
          currentCache = newItems;
        } else if (
          !other.arg.search ||
          (other.arg.search && other.arg.offset)
        ) {
          currentCache.push(...newItems);
        }
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg;
      },
      providesTags: (_) => ["BySubject"],
    }),
    getCoursesByChapter: builder.query({
      query: ({
        batchID,
        learningMode,
        classID,
        search,
        chapterID,
        subjectID,
        limit,
        offset,
      }) => ({
        url: `get_course_chapter`,
        params: {
          batch_id: batchID,
          learning_mode: learningMode,
          class_id: classID,
          search,
          chapter_id: chapterID,
          subject_id: subjectID,
          limit,
          offset,
        },
      }),
      transformResponse: (response, meta, arg) => response.data,
      serializeQueryArgs: ({ queryArgs }) => {
        const newQueryArgs = { ...queryArgs };

        if (newQueryArgs.limit) {
          delete newQueryArgs.limit;
          delete newQueryArgs.offset;
        }

        return newQueryArgs;
      },
      merge: (currentCache, newItems, other) => {
        if (!other.arg.offset) {
          currentCache = newItems;
        } else if (
          !other.arg.search ||
          (other.arg.search && other.arg.offset)
        ) {
          currentCache.push(...newItems);
        }
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg;
      },
      providesTags: (_) => ["ByChapter"],
    }),
    getCoursesByGroup: builder.query({
      query: ({ batchID, learningMode, classID, search, limit, offset }) => ({
        url: `get_course_group`,
        params: {
          batch_id: batchID,
          learning_mode: learningMode,
          class_id: classID,
          search,
          limit,
          offset,
        },
      }),
      transformResponse: (response, meta, arg) => response.data,
      serializeQueryArgs: ({ queryArgs }) => {
        const newQueryArgs = { ...queryArgs };

        if (newQueryArgs.limit) {
          delete newQueryArgs.limit;
          delete newQueryArgs.offset;
        }

        return newQueryArgs;
      },
      merge: (currentCache, newItems, other) => {
        if (!other.arg.offset) {
          currentCache = newItems;
        } else if (
          !other.arg.search ||
          (other.arg.search && other.arg.offset)
        ) {
          currentCache.push(...newItems);
        }
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg;
      },
      providesTags: (_) => ["ByGroup"],
    }),
    getSubjectChapterListing: builder.query({
      query: ({ batchID, learningMode, classID }) => ({
        url: `subject_chapter`,
        params: {
          batch_id: batchID,
          learning_mode: learningMode,
          class_id: classID,
        },
      }),
      transformResponse: (response, meta, arg) => response.data,
    }),
    getNewCourses: builder.query({
      query: () => ({
        url: `new_course`,
      }),
      transformResponse: (response, meta, arg) => response.data,
    }),
    getNewCourseDetail: builder.query({
      query: (class_id) => ({
        url: `new_course_details`,
        params: {
          class_id,
        },
      }),
      transformResponse: (response, meta, arg) => response,
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetFreeResourcesQuery,
  useGetStudentFeedbackQuery,
  useGetCourseDetailQuery,
  useGetCoursePlansQuery,
  useGetUltimateLearningQuery,
  useGetCoursesBySubjectQuery,
  useGetCoursesByChapterQuery,
  useGetCoursesByGroupQuery,
  useGetSubjectChapterListingQuery,
  useGetNewCoursesQuery,
  useGetNewCourseDetailQuery,
} = coursesData;
